/**
 * Halaman Bio / link-in-bio (page-templates/template-bio.php).
 *
 * Diporting dari proyek dufeng staging, yang aslinya pakai Swiper - di sini
 * semua slider (pager utama + tiga blok konten yang bisa digeser) pakai
 * Splide, satu-satunya slider library yang dipakai proyek ini (lihat
 * ./sliders.js). Konfigurasi per-blok (speed/autoplay/delay dari ACF, lihat
 * template-parts/content-bio.php) diteruskan lewat data-attribute
 * (data-speed/data-autoplay/data-interval), bukan inline <script> seperti
 * di sumbernya.
 */

import { Splide } from "@splidejs/splide";

export function initBioPage() {
  const root = document.querySelector(".dufeng-bio");
  if (!root) return;

  initBioPager(root);
  initBioContentSliders(root);
  initBioTextRotators(root);
}

/**
 * Pager utama: satu "halaman" (baris repeater ACF "page") per slide. Sengaja
 * tidak bisa digeser sentuh (drag: false) - hanya lewat panah/pagination di
 * bar bawah, sama seperti sumbernya (Swiper allowTouchMove:false). Splide
 * tidak punya autoHeight built-in yang menyamai Swiper, jadi tinggi track
 * disamakan manual ke slide aktif tiap kali pindah.
 */
function initBioPager(root) {
  const pagerEl = root.querySelector(".dufeng-bio__pager");
  if (!pagerEl) return;

  const navEl = root.querySelector(".dufeng-bio__nav");
  const slideCount = pagerEl.querySelectorAll(".splide__slide").length;

  const pager = new Splide(pagerEl, {
    type: "slide",
    perPage: 1,
    perMove: 1,
    drag: false,
    arrows: slideCount > 1,
    pagination: true,
    rewind: false,
  });

  const syncHeight = () => {
    const activeSlide = pagerEl.querySelector(".splide__slide.is-active");
    const track = pagerEl.querySelector(".splide__track");
    if (activeSlide && track) {
      track.style.height = `${activeSlide.offsetHeight}px`;
    }
  };

  pager.on("mounted", () => {
    syncHeight();

    // Panah & pagination dibuat otomatis oleh Splide sebagai anak langsung
    // .splide (sibling dari .splide__track) - dipindah ke bar fixed
    // .dufeng-bio__nav supaya jadi satu bar terpadu di bawah viewport.
    if (navEl) {
      const arrows = pagerEl.querySelector(".splide__arrows");
      const pagination = pagerEl.querySelector(".splide__pagination");
      if (arrows) navEl.appendChild(arrows);
      if (pagination) navEl.appendChild(pagination);
    }
  });

  pager.on("moved", syncHeight);
  window.addEventListener("resize", syncHeight);

  pager.mount();
}

/**
 * Tiga blok konten yang punya slider sendiri: bio-slider.php
 * (.dufeng-bio-slider), bio-product_items.php style-1
 * (.dufeng-bio-product-slider), bio-review.php (.dufeng-bio-review-slider).
 * Konfigurasi speed/autoplay/interval dibaca dari data-attribute yang
 * di-render PHP dari sub field "Slider Options" ACF.
 */
function initBioContentSliders(root) {
  initSplideGroup(root, ".dufeng-bio-slider", (element) => ({
    type: "loop",
    perPage: 1,
    perMove: 1,
    arrows: false,
    pagination: true,
    speed: readSpeed(element, 1100),
    autoplay: readAutoplay(element),
    interval: readInterval(element, 4500),
    pauseOnHover: false,
  }));

  // Desktop 3 kolom penuh (perPage integer, aman buat Splide). Tablet/mobile
  // pakai autoWidth: true supaya lebar slide (termasuk potongan slide
  // berikutnya sebagai petunjuk bisa digeser) murni ditentukan CSS
  // (.dufeng-bio-product-slider .splide__slide width, lihat _bio.scss) -
  // perPage pecahan (2.2 / 1.2) sebelumnya bikin batas geser Splide
  // salah hitung sehingga drag/swipe macet.
  initSplideGroup(root, ".dufeng-bio-product-slider", (element) => ({
    type: "loop",
    perPage: 3,
    perMove: 3,
    gap: "16px",
    arrows: false,
    pagination: true,
    speed: readSpeed(element, 1100),
    autoplay: readAutoplay(element),
    interval: readInterval(element, 4500),
    rewind: true,
    pauseOnHover: false,
    breakpoints: {
      991: { perPage: 1, autoWidth: true, perMove: 1, gap: "25px" },
      767: { perPage: 1, autoWidth: true, perMove: 1, gap: "25px" },
    },
  }));

  initSplideGroup(root, ".dufeng-bio-review-slider", (element) => {
    const slideCount = element.querySelectorAll(".splide__slide").length;
    return {
      type: "fade",
      rewind: true,
      perPage: 1,
      perMove: 1,
      arrows: slideCount > 1,
      pagination: slideCount > 1,
      speed: readSpeed(element, 900),
      autoplay: readAutoplay(element),
      interval: readInterval(element, 3500),
      pauseOnHover: false,
    };
  });
}

function initSplideGroup(root, selector, buildOptions) {
  const elements = root.querySelectorAll(selector);
  if (!elements.length) return;

  elements.forEach((element) => {
    new Splide(element, buildOptions(element)).mount();
  });
}

function readSpeed(element, fallback) {
  const value = parseInt(element.dataset.speed, 10);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function readInterval(element, fallback) {
  const value = parseInt(element.dataset.interval, 10);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function readAutoplay(element) {
  return element.dataset.autoplay === "1";
}

/**
 * Blok "Topbar" (template-parts/bio-topbar.php): teks statis diikuti baris
 * teks yang berganti (elemen <b>, satu "is-visible" pada satu waktu).
 * Mengganti vendor ha-animated-text.js yang dipakai sumbernya - cukup toggle
 * class, transisinya sendiri di CSS (section/_bio.scss).
 */
function initBioTextRotators(root) {
  const rotators = root.querySelectorAll("[data-bio-text-rotator]");
  if (!rotators.length) return;

  rotators.forEach((rotator) => {
    const lines = Array.from(rotator.children);
    if (lines.length < 2) return;

    let activeIndex = lines.findIndex((line) => line.classList.contains("is-visible"));
    if (activeIndex < 0) activeIndex = 0;

    setInterval(() => {
      lines[activeIndex].classList.remove("is-visible");
      lines[activeIndex].classList.add("is-hidden");

      activeIndex = (activeIndex + 1) % lines.length;

      lines[activeIndex].classList.remove("is-hidden");
      lines[activeIndex].classList.add("is-visible");
    }, 2800);
  });
}
